import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { ContentfulEntry } from "frontend/contentful/schema/sys";
import {
  AudienceBlocksWrapperEntry,
  PageSectionThemeEntry,
} from "frontend/contentful/schema/blocks";
import {
  ArchivePageEntry,
  ArticlePageEntry,
  DetailPageEntry,
  KnowledgeBaseArticlePageEntry,
  LandingPageEntry,
  LandingPageBlockEntry,
  ArchivePageBlockEntry,
  PreFooterBlockEntry,
  DatabasePageEntry,
  DatasetPageEntry,
  EventDetailPageEntry,
  IssueLandingPageEntry,
  CoursePageEntry,
  PodcastEpisodePageEntry,
} from "frontend/contentful/schema/pages";
import {
  ArticlePageSettingsEntry,
  IssueLandingPageSettingsEntry,
} from "frontend/contentful/schema/universal";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { PageSection } from "design-system/components/layouts/page-section/page-section";
import { RenderContentfulEntry } from "../utils/render-contentful-entry";
import { SimpleTheme } from "design-system/utils/theme";
import { useContentful } from "frontend/hooks/use-contentful";
import { ReactNode } from "react";
import { usePageProps } from "frontend/hooks/use-page-props";

interface PageSectionBlocksProps {
  theme?: SimpleTheme;
  blocks: Array<ContentfulEntry>;
}

type PageSectionEntries =
  | LandingPageEntry
  | IssueLandingPageEntry
  | KnowledgeBaseArticlePageEntry
  | ArchivePageEntry
  | ArticlePageEntry
  | DetailPageEntry
  | ArticlePageSettingsEntry
  | IssueLandingPageSettingsEntry
  | DatabasePageEntry
  | DatasetPageEntry
  | EventDetailPageEntry
  | CoursePageEntry
  | PodcastEpisodePageEntry;

interface Options {
  key: "topContent" | "preFooterContent";
  hideFAEmbeds?: boolean;
}

export const ContentfulPageSections = createContentfulComponent<
  PageSectionEntries,
  Options
>(({ entry, options }) => {
  const { getEntries } = useContentful();

  const pageProps = usePageProps();
  const sections: Array<PageSectionBlocksProps> = [];

  const isKnowledgeBaseArticlePage =
    isContentType<KnowledgeBaseArticlePageEntry>(
      "knowledgeBaseArticlePage",
      entry,
    );
  const isArchivePage = isContentType<ArchivePageEntry>("archivePage", entry);
  const isArticlePage = isContentType<ArticlePageEntry>("articlePage", entry);
  const isDetailPage = isContentType<DetailPageEntry>("detailPage", entry);
  const isDatabasePage = isContentType<DatabasePageEntry>(
    "databasePage",
    entry,
  );
  const isDatasetPage = isContentType<DatasetPageEntry>("datasetPage", entry);
  const isArticlePageSettings = isContentType<ArticlePageSettingsEntry>(
    "articlePageSettings",
    entry,
  );
  const isIssueLandingPageSettings =
    isContentType<IssueLandingPageSettingsEntry>(
      "issueLandingPageSettings",
      entry,
    );
  const isIssueLandingPage = isContentType<IssueLandingPageEntry>(
    "issueLandingPage",
    entry,
  );
  const isCoursePage = isContentType<CoursePageEntry>("coursePage", entry);
  const isPodcastEpisodePage = isContentType<PodcastEpisodePageEntry>(
    "podcastEpisodePage",
    entry,
  );

  let blocks:
    | Array<LandingPageBlockEntry>
    | Array<ArchivePageBlockEntry>
    | Array<PreFooterBlockEntry>;

  if (isKnowledgeBaseArticlePage) {
    blocks = getEntries(entry.fields.body);
  } else if (isArchivePage) {
    if (options?.key) {
      blocks = getEntries(entry.fields[options.key]);
    } else {
      blocks = [];
    }
  } else if (
    isArticlePage ||
    isDetailPage ||
    isDatabasePage ||
    isDatasetPage ||
    isArticlePageSettings ||
    isIssueLandingPage ||
    isIssueLandingPageSettings ||
    isCoursePage ||
    isPodcastEpisodePage ||
    isContentType<EventDetailPageEntry>("eventDetailPage", entry)
  ) {
    blocks = getEntries(entry.fields.preFooterContent);
  } else {
    blocks = getEntries(entry.fields.blocks);
  }

  const userRoles = pageProps.session?.user.roles;

  // If there is an audience set, return all blocks from block wrappers that match the audience (excluding blocks without a wrapper)
  // If there is no audience match, use the public blocks.

  if (userRoles?.length) {
    const blockWrappers = blocks?.filter(
      (block): block is AudienceBlocksWrapperEntry => {
        if (
          isContentType<AudienceBlocksWrapperEntry>(
            "audienceBlocksWrapper",
            block,
          ) &&
          block.fields.permissions
        ) {
          const blockPermissions = getEntries(block.fields.permissions);

          const userIsAuthorized = blockPermissions.some((blockPermission) => {
            const role = blockPermission.fields.roleCode;
            const roleValues = role?.split(",") ?? [];

            return roleValues.some((r) => userRoles.includes(r.trim()));
          });

          return userIsAuthorized;
        }
        return false;
      },
    );

    if (blockWrappers?.length) {
      blocks = getEntries(blockWrappers[0]?.fields.blocks) || [];
    }
  }

  blocks?.forEach((block) => {
    if (isContentType<PageSectionThemeEntry>("pageSectionTheme", block)) {
      sections.push({ theme: block.fields.theme, blocks: [] });
    } else {
      if (sections.length === 0) {
        sections.push({
          blocks: [block],
          theme: isKnowledgeBaseArticlePage ? "white" : undefined,
        });
      } else {
        sections[sections.length - 1]?.blocks.push(block);
      }
    }
  });

  return (
    <>
      {sections.map(({ theme, blocks }, i) => {
        if (blocks.length < 1) return null;

        const blockTotal: Array<ReactNode> = [];
        let sectionHeaderAbove = false;
        let headerAbove = false;

        blocks.forEach((block, i) => {
          let entryHeaderAbove = headerAbove || sectionHeaderAbove;
          if (
            block.sys.contentType.sys.id === "sideBySideSectionIntro" &&
            sectionHeaderAbove
          ) {
            entryHeaderAbove = true;
          } else if (
            block.sys.contentType.sys.id === "sideBySideSectionIntro" &&
            !sectionHeaderAbove
          ) {
            entryHeaderAbove = false;
          }

          let resetHeaders = undefined;
          if (
            block.sys.contentType.sys.id.includes("teaseRow") &&
            sectionHeaderAbove &&
            headerAbove
          ) {
            resetHeaders = "h4";
          }

          let push = true;
          if (
            block.sys.contentType.sys.id === "formAssemblyEmbed" &&
            options?.hideFAEmbeds === true
          ) {
            push = false;

            if (isCoursePage) {
              console.warn(
                `Course Page ${entry.fields.title} has more than one Form Assembly Embed so the Embeds are currently hidden.`,
              );
            } else {
              console.warn(
                `Page has more than one Form Assembly Embed so the Embeds are currently hidden.`,
              );
            }
          }

          if (push) {
            const options = { headerAbove: entryHeaderAbove, resetHeaders };
            blockTotal.push(
              <RenderContentfulEntry key={i} entry={block} options={options} />,
            );
          }
          // As long as I'm in the same section and BG hasn't changed assume section header or side by side Section Intro is the parent to this whole section
          if (block.sys.contentType.sys.id === "sideBySideSectionIntro") {
            headerAbove = true;
          } else if (block.sys.contentType.sys.id === "pageSectionHeader") {
            sectionHeaderAbove = true;
          }
        });

        return (
          <PageSection key={i} theme={theme}>
            {blockTotal}
          </PageSection>
        );
      })}
    </>
  );
});
